import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import findLastKey from 'lodash/findLastKey';
import { saveBankAccount } from 'actions/selfservice';
import { track } from 'actions/analytics';
import formValueSelector from 'redux-form/es/formValueSelector';
import WithdrawalForm from 'components/Credit24/SelfService/Withdrawal/WithdrawalForm';
import WithdrawalRangeForm from 'components/Credit24/SelfService/Withdrawal/WithdrawalRangeForm';
import WithdrawalConfirmation from 'components/Credit24/SelfService/Withdrawal/WithdrawalConfirmation';
import WithdrawalIBANConfirmation from 'components/Credit24/SelfService/Withdrawal/WithdrawalIBANConfirmation';
import WithdrawalFeedback from 'components/Credit24/SelfService/Withdrawal/WithdrawalFeedback';
import WithdrawalBlocked from 'components/Credit24/SelfService/Withdrawal/WithdrawalBlocked';
import PositionedOverlay from 'components/Credit24/SelfService/Overlays/PositionedOverlay';
import { fromCents, isMobile, toCents } from 'utils/helpers';
import { maturityPeriods } from 'consts';

const propTypes = {
  selfservice: PropTypes.object,
  contract: PropTypes.object.isRequired,
  withdrawalRef: PropTypes.any,
  sendDrawAmount: PropTypes.func,
  saveBankAccount: PropTypes.func,
  withdrawalAmountIndex: PropTypes.number,
  withdrawalAmountBox: PropTypes.string,
  bankAccount: PropTypes.string,
  isUpgradeHighlighted: PropTypes.bool,
  customerNumber: PropTypes.string,
  track: PropTypes.func,
};

class Withdrawal extends Component {
  constructor() {
    super();
    this.state = {
      mmp: 0,
      isConfirmationOpen: false,
      isWithdrawalStarted: false,
    };
    this.onWithdrawalClick = this.onWithdrawalClick.bind(this);
    this.onCancelClick = this.onCancelClick.bind(this);
    this.onConfirmationClick = this.onConfirmationClick.bind(this);
    this.onIBANSubmitClick = this.onIBANSubmitClick.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { withdrawalAmountIndex, withdrawalAmountBox, contract: { mmps } } = nextProps;

    if ((this.props.withdrawalAmountIndex !== withdrawalAmountIndex)
      || (this.props.withdrawalAmountBox !== withdrawalAmountBox)
    ) {
      let mmp = 0;
      if (withdrawalAmountIndex >= 0) {
        mmp = fromCents(mmps[withdrawalAmountIndex].mmp);
      } else if (withdrawalAmountBox) {
        const amount = parseInt(withdrawalAmountBox, 10);
        const mmpKey = findLastKey(mmps, (item) => item.sum === amount);
        if (mmpKey) {
          mmp = fromCents(mmps[mmpKey].mmp);
        }
      }
      this.setState({
        mmp,
      });
    }
  }

  onWithdrawalClick() {
    this.setState({
      isConfirmationOpen: true,
    });
    const { withdrawalAmountIndex, contract: { mmps } } = this.props;
    const amount = mmps[withdrawalAmountIndex].sum;

    this.props.track('DrawSelfservice', { amount });
  }

  onCancelClick() {
    this.setState({
      isConfirmationOpen: false,
      isWithdrawalStarted: false,
    });
  }

  onConfirmationClick() {
    const {
      sendDrawAmount, contract, withdrawalAmountIndex, withdrawalAmountBox,
    } = this.props;
    sendDrawAmount(contract.id, withdrawalAmountBox || contract.mmps[withdrawalAmountIndex].sum);
    this.setState({
      isWithdrawalStarted: true,
    });
  }

  onIBANSubmitClick(values) {
    this.props.saveBankAccount(values);
  }

  render() {
    const {
      contract,
      selfservice,
      withdrawalRef,
      bankAccount,
      withdrawalAmountIndex,
      withdrawalAmountBox,
      isUpgradeHighlighted,
      customerNumber,
    } = this.props;
    const { isWithdrawalStarted, isConfirmationOpen, mmp } = this.state;
    const { customerBankName } = selfservice;

    if (contract.drawBlockedReason && !isConfirmationOpen) {
      return (
        <WithdrawalBlocked
          customerNumber={customerNumber}
          drawBlockedReason={contract.drawBlockedReason}
        />
      );
    }

    let frequency = null;
    if (COUNTRY === 'AU') {
      frequency = (
        <FormattedMessage
          id={maturityPeriods[contract.product.maturityPeriodLength]}
        />
      );
    }

    if (!withdrawalRef || !contract.mmps) {
      return null;
    }

    const { mmps } = contract;
    let withdrawalAmount = fromCents(withdrawalAmountBox);
    if (withdrawalAmountIndex >= 0) {
      withdrawalAmount = fromCents(mmps[withdrawalAmountIndex].sum);
    }

    const showDelayText = COUNTRY === 'FI' && (customerBankName === 'SPankki' || customerBankName === 'Aktia');

    let overlayContent = (
      <WithdrawalConfirmation
        mmp={mmp}
        bankAccount={bankAccount}
        withdrawalAmount={withdrawalAmount}
        onCancelClick={this.onCancelClick}
        onConfirmationClick={this.onConfirmationClick}
        frequency={frequency}
        showDelayText={showDelayText}
      />
    );
    if (isWithdrawalStarted) {
      overlayContent = (
        <WithdrawalFeedback
          {...selfservice}
          mmp={mmp}
          bankAccount={bankAccount}
          withdrawalAmount={withdrawalAmount}
          onCancelClick={this.onCancelClick}
          onConfirmationClick={this.onConfirmationClick}
        />
      );
    } else if (!bankAccount) {
      overlayContent = (
        <WithdrawalIBANConfirmation
          onCancelClick={this.onCancelClick}
          onIBANSubmitClick={this.onIBANSubmitClick}
        />
      );
    }

    return (
      <>
        {
          isMobile ? (
            <WithdrawalForm
              contract={contract}
              onWithdrawalClick={this.onWithdrawalClick}
            />
          ) : (
            <WithdrawalRangeForm
              mmp={mmp}
              contract={contract}
              onWithdrawalClick={this.onWithdrawalClick}
              isUpgradeHighlighted={isUpgradeHighlighted}
              frequency={frequency}
            />
          )
        }
        <PositionedOverlay
          key="withdrawalConfirmation"
          nodeRef={withdrawalRef}
          isOpen={isConfirmationOpen}
        >
          {overlayContent}
        </PositionedOverlay>
      </>
    );
  }
}

Withdrawal.propTypes = propTypes;

const selector = formValueSelector('withdrawalForm');

export default connect(
  (state) => ({
    withdrawalAmountIndex: selector(state, 'withdrawalAmountIndex'),
    withdrawalAmountBox: (
      selector(state, 'withdrawalAmountBox')
      || (
        selector(state, 'withdrawalCustomAmountBox')
        && toCents(selector(state, 'withdrawalCustomAmountBox'), 'float').toString()
      )
    ),
  }),
  {
    saveBankAccount,
    track,
  }
)(Withdrawal);
